import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/PageHeader";
import { RichEditor } from "@/components/RichEditor";
import { api, errMsg, fmtDateTime, BOAT_CATEGORIES, MAX_FILE, slug } from "@/lib/api";

const PDF_MAX = 10 * 1024 * 1024;

const ComForm = ({ onSent }) => {
  const [oggetto, setOggetto] = useState("");
  const [testo, setTesto] = useState("");
  const [dest, setDest] = useState("tutti");
  const [foto, setFoto] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [key, setKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (f && (!/\.(jpe?g|png)$/i.test(f.name) || f.size > MAX_FILE)) {
      e.target.value = "";
      return toast.error("Foto JPG o PNG, massimo 2 MB");
    }
    setFoto(f || null);
  };

  const pickPdf = (e) => {
    const f = e.target.files?.[0];
    if (f && (!/\.pdf$/i.test(f.name) || f.size > PDF_MAX)) {
      e.target.value = "";
      return toast.error("Allegato PDF, massimo 10 MB");
    }
    setPdf(f || null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!testo) return toast.error("Scrivi il testo della comunicazione");
    const fd = new FormData();
    fd.append("oggetto", oggetto);
    fd.append("testo", testo);
    fd.append("destinatari", dest);
    if (foto) fd.append("foto", foto);
    if (pdf) fd.append("allegato", pdf);
    setSaving(true);
    try {
      const { data } = await api.post("/admin/comunicazioni", fd);
      toast.success(`Comunicazione inviata a ${data.recipients_count} ${data.recipients_count === 1 ? "tesserato" : "tesserati"}`);
      setOggetto(""); setTesto(""); setFoto(null); setPdf(null); setKey((k) => k + 1);
      onSent();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="comunicazione-form">
      <div>
        <Label>Destinatari (tesserati anno in corso)</Label>
        <Select value={dest} onValueChange={setDest}>
          <SelectTrigger data-testid="comunicazione-dest-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti" data-testid="comunicazione-dest-option-tutti">Tutti i tesserati</SelectItem>
            {BOAT_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`comunicazione-dest-option-${slug(c)}`}>Classe {c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div><Label>Oggetto</Label><Input required value={oggetto} onChange={(e) => setOggetto(e.target.value)} data-testid="comunicazione-oggetto-input" /></div>
      <div><Label>Testo</Label><RichEditor value={testo} onChange={setTesto} minHeight={220} testid="comunicazione-testo-input" /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Foto (facoltativa, JPG o PNG max 2 MB)</Label><Input key={key} type="file" accept=".jpg,.jpeg,.png" onChange={pick} data-testid="comunicazione-foto-input" /></div>
        <div><Label>Allegato PDF (facoltativo, max 10 MB)</Label><Input key={`p${key}`} type="file" accept=".pdf" onChange={pickPdf} data-testid="comunicazione-pdf-input" /></div>
      </div>
      <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="comunicazione-submit-button">{saving ? "Invio…" : "Invia comunicazione"}</Button>
    </form>
  );
};

export const ComunicazioniAdmin = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/comunicazioni").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <Panel>
        <h2 className="font-heading text-2xl uppercase mb-1">Nuova comunicazione</h2>
        <p className="text-sm text-muted-foreground mb-5">I tesserati la ricevono nell'app: notifica sulla campanella e pagina Comunicazioni.</p>
        <ComForm onSent={load} />
      </Panel>
      <Panel>
        <h2 className="font-heading text-2xl uppercase mb-4">Inviate</h2>
        {items.length === 0 && <p className="text-muted-foreground">Nessuna comunicazione inviata.</p>}
        <div className="divide-y">
          {items.map((c) => (
            <div key={c.id} className="py-3" data-testid={`comunicazione-admin-row-${c.id}`}>
              <div className="font-medium">{c.oggetto}</div>
              <div className="text-xs text-muted-foreground">{fmtDateTime(c.created_at)} · {c.destinatari === "tutti" ? "Tutti i tesserati" : `Classe ${c.destinatari}`} · {c.recipients_count} {c.recipients_count === 1 ? "destinatario" : "destinatari"}{c.foto_id ? " · con foto" : ""}{c.allegato_id ? " · con PDF" : ""}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};
